import { z } from 'zod';
/**
 * FactProvenance — the wire shape every AI-learned fact carries so the
 * portal can render its source, confidence, scan history, the 4-state
 * authored/confirmed/inferred/stale taxonomy, and the rich
 * `<FactTimeline>` event history.
 *
 * Per CLAUDE.md "Every AI-generated fact carries provenance + confidence"
 * — this schema is the single source of truth that `<SourceBadge>`,
 * `<ConfidenceMeter>`, `<TrustScoreBar>`, `<AgePill>`, and the
 * `<FactTimeline>` slide-over (issue #166 PR A — agent trust loop) all
 * consume, and that the LLM synthesis layer stamps onto each learned
 * attribute (person / company / dossier).
 *
 * Schema is read by:
 *  - server-side synthesis (`apps/functions/src/synthesis/**`) writes
 *    the FactProvenance onto each fact at synthesis time.
 *  - portal renderers (profile inline-edit, dossier rails, briefing
 *    hero cards) read it to draw the source pill, confidence meter, and
 *    "appears on" surface chips.
 *  - the new `<FactTimeline>` reads `events[]` to render the per-fact
 *    history list with weight + trust-score per event.
 *  - the writer caps `scanHistory` to the most recent 10 entries and
 *    `events` to the most recent 50 before persisting — bounded growth
 *    keeps the per-fact doc small.
 */
/** Source channel that produced an AI-learned fact about a user / company / dossier. */
export const FactSource = z.enum([
    'told', // user typed it in (onboarding / profile inline-edit)
    'web', // public web research (Vertex grounded synthesis)
    'gmail', // Google Gmail connector
    'gcal', // Google Calendar connector
    'slack', // Slack connector
    'salesforce', // Salesforce CRM
    'github', // GitHub
    'stripe', // Stripe
    'linkedin', // Direct LinkedIn snippet attribution when grounded search returns a LinkedIn URL
    'crunchbase', // Crunchbase
    'apollo', // Apollo
    'voice-notes', // CEO-captured voice memos (issue #166 — high-trust ground truth)
    'written-notes', // CEO-captured written notes
    'notion', // Notion connector
    'drive', // Google Drive connector
    'agent-inference', // Synthesized inference with no external grounding
    'external-web', // General web search (non-grounded)
]);
/**
 * FactState — 4-state UI taxonomy for the dossier + fact-timeline UI
 * (issue #166 PR A). When omitted on a legacy fact the renderer defaults
 * to `'inferred'` so old documents don't visually claim higher trust
 * than they carry.
 *
 *  - authored:  the user typed/spoke it themselves (highest trust)
 *  - confirmed: the user explicitly confirmed an agent-inferred fact
 *  - inferred:  the agent inferred from connector signals; no user confirm
 *  - stale:     last refresh exceeded the per-fact freshness window
 */
export const FactState = z.enum(['authored', 'confirmed', 'inferred', 'stale']);
/**
 * FactHistoryEvent — one row in the `<FactTimeline>` slide-over for a
 * given fact. Captures what the agent saw, when, where it came from, the
 * per-event trust score, and the categorical weight used by the
 * SourceWeightSlider page (high / med / low). Bounded to the most recent
 * 50 entries by the writer.
 */
export const FactHistoryEvent = z.object({
    ts: z.string().datetime(),
    source: FactSource,
    value: z.string(),
    trustScore: z.number().min(0).max(1),
    weight: z.enum(['high', 'med', 'low']),
});
/** Surface a learned fact appears on — drives the "where this appears" pill row in profile UI. */
export const FactSurface = z.enum([
    'briefing',
    'profile',
    'profile-company',
    'dossier-overview',
    'dossier-graph',
    'ask',
    'rocks',
    'scorecard',
    'insights',
]);
export const FactProvenance = z.object({
    source: FactSource,
    /** 0..1, the LLM/grounded synthesis's self-rated confidence. */
    confidence: z.number().min(0).max(1),
    /** ISO timestamp when this fact was last refreshed. */
    lastScanned: z.string().datetime(),
    /** History of scan timestamps (most recent last). Capped to 10 entries by the writer. */
    scanHistory: z.array(z.string().datetime()).max(10).default([]),
    /** Surfaces that read this fact, so the profile UI can render "where this appears" pills. */
    whereUsed: z.array(FactSurface).default([]),
    /**
     * 4-state taxonomy (issue #166 PR A). Optional for backwards-compat
     * with facts written before the state machine landed; the renderer
     * treats `undefined` as `'inferred'` so legacy facts don't visually
     * over-claim trust.
     */
    state: FactState.optional(),
    /**
     * Rich event history for the `<FactTimeline>` slide-over (issue #166
     * PR A). Capped to 50 entries by the writer. Defaults to `[]` for
     * facts that haven't accumulated history yet.
     */
    events: z.array(FactHistoryEvent).max(50).default([]),
});
